import { action, computed, toJS } from 'mobx'
import { ModalViewModel } from '@/infra/ModalViewModel'
import { type Job } from '@/features/jobs/Job'
import { type JobStore } from '@/features/jobs/JobStore'
import { type Member } from '@/features/iam/members/Member'
import type { AlertErrorHandlerFactory } from '@/features/shell'
import {
  FinalReturnAmountsDialogViewModel,
  returnAmountsToYearInputs,
} from '@/features/jobs/details/components/FinalReturnAmountsDialog/FinalReturnAmountsDialogViewModel'
import { hasManualAmount, netAmount } from './return-amounts'

/**
 * Details-view card showing the per-tax-year final return amounts recorded
 * when the Pro filed the return, with an edit affordance for the assigned Pro
 * and workspace admins.
 */
export class FinalReturnAmountsCardViewModel {
  readonly dialogVm: FinalReturnAmountsDialogViewModel

  /**
   * The per-year breakdown of the amounts.
   */
  readonly breakdownModal = new ModalViewModel()

  constructor(
    private readonly _job: Job,
    private readonly _jobStore: JobStore,
    createAlertErrorHandler: AlertErrorHandlerFactory,
    private readonly _getMember: () => Member,
  ) {
    this.dialogVm = new FinalReturnAmountsDialogViewModel(
      this._jobStore,
      this._job,
      createAlertErrorHandler,
      () => this._onSaved(),
      () => returnAmountsToYearInputs(this._job.returnAmounts),
    )
  }

  /**
   * The card is hidden entirely until amounts exist — most jobs never reach a
   * final return, and an empty card on all of them is noise.
   */
  @computed
  get canView() {
    return this._job.hasReturnAmounts
  }

  /**
   * The recorded amounts, per tax year.
   */
  @computed
  get years() {
    return toJS(this._job.returnAmounts)
  }

  /**
   * The states with amounts in any tax year, in alphabetical order.
   */
  @computed
  get states() {
    return Array.from(
      new Set(this.years.flatMap((y) => y.stateAmounts.map((s) => s.state))),
    ).sort()
  }

  /**
   * The net amount across all tax years.
   */
  @computed
  get net() {
    return this.years.reduce((sum, year) => sum + netAmount(year), 0)
  }

  /**
   * How many tax years have an amount that was entered manually.
   */
  @computed
  get manualCount() {
    return this.years.filter(hasManualAmount).length
  }

  /**
   * Whether the current member may edit the amounts.
   */
  @computed
  get canEdit() {
    return (
      this._job.isProviderCollaborator ||
      this._getMember().permissions.adminJobUpdate
    )
  }

  /**
   * Closes the breakdown, if open, and opens the dialog for editing the
   * amounts.
   */
  @action.bound
  edit() {
    this.breakdownModal.close()
    this.dialogVm.show()
  }

  /**
   * Realtime job-updated events carry only Last Activity, so a save has to
   * refetch the job for the card to reflect it.
   */
  private async _onSaved() {
    // The amounts are recorded; a failed refetch only leaves the card stale.
    await this._jobStore
      .fetchJob(this._job.id, { force: true })
      .catch(() => undefined)
  }
}
